import React from 'react';

import { cn } from '@/lib/utils';
import type { TotadminPermission } from 'data/__generated__';
import { usePermissionGate } from 'hooks/useHasPermission';
import { PermissionLockIcon, PermissionLockWrapper } from 'components/PermissionLock';

type TotemPrimaryButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
    $isSecondaryStyle?: boolean;
    $isDangerStyle?: boolean;
    $minWidth?: string;
    /** Droits fins TOUS requis pour agir. Sans eux : bouton grisé, cadenas, infobulle, clic bloqué. */
    requiredPermissions?: readonly TotadminPermission[];
};

export const TotemPrimaryButton = React.forwardRef<HTMLButtonElement, TotemPrimaryButtonProps>(
    (
        {
            $isSecondaryStyle,
            $isDangerStyle,
            $minWidth,
            className,
            style,
            requiredPermissions,
            disabled,
            children,
            ...props
        },
        ref,
    ) => {
        const { isAllowed, missing } = usePermissionGate(requiredPermissions);

        const button = (
            <button
                ref={ref}
                className={cn(
                    'inline-flex h-11 shrink-0 cursor-pointer items-center justify-center gap-1.5 rounded-lg px-5 text-[15px] font-extrabold uppercase tracking-[1.5px] transition-all select-none hover:opacity-85 disabled:cursor-default disabled:opacity-50',
                    $isDangerStyle
                        ? 'bg-destructive text-white'
                        : $isSecondaryStyle
                          ? 'bg-zinc-200 text-secondary-foreground hover:bg-zinc-300'
                          : 'bg-primary text-primary-foreground',
                    className,
                )}
                style={{ ...(style || {}), ...($minWidth ? { minWidth: $minWidth } : {}) }}
                disabled={disabled || !isAllowed}
                {...props}
            >
                {!isAllowed ? <PermissionLockIcon /> : null}
                {children}
            </button>
        );

        return isAllowed ? button : <PermissionLockWrapper missing={missing}>{button}</PermissionLockWrapper>;
    },
);
TotemPrimaryButton.displayName = 'TotemPrimaryButton';
